import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ChevronDown, Battery, Droplets, Smartphone } from 'lucide-react';

const productImages = [
  { name: 'Midnight Black', hex: '#1A1A1A', image: 'https://customer-assets.emergentagent.com/job_27a04b8e-bf34-46a5-a674-e3b31bbd3a27/artifacts/009zy0w9_fitness%20band%20black.png' },
  { name: 'Cloud White', hex: '#F5F5F5', image: 'https://customer-assets.emergentagent.com/job_connect-premium-2/artifacts/57600vp7_fitness%20band%20white.png' },
  { name: 'Sand Beige', hex: '#E1C699', image: 'https://customer-assets.emergentagent.com/job_27a04b8e-bf34-46a5-a674-e3b31bbd3a27/artifacts/9ax0e27d_Fitness%20band%20bienge.png' },
  { name: 'Sky Blue', hex: '#87CEEB', image: 'https://customer-assets.emergentagent.com/job_connect-premium-2/artifacts/dzn00r33_fitness%20band%20sky%20blue.png' },
  { name: 'Soft Pink', hex: '#FFB6C1', image: 'https://customer-assets.emergentagent.com/job_27a04b8e-bf34-46a5-a674-e3b31bbd3a27/artifacts/3bgjybb4_fitness%20band%20pink.png' },
  { name: 'Ocean Blue', hex: '#0047AB', image: 'https://customer-assets.emergentagent.com/job_connect-premium-2/artifacts/av2155rf_fitness%20band%20blue.png' },
  { name: 'Crimson Red', hex: '#DC143C', image: 'https://customer-assets.emergentagent.com/job_connect-premium-2/artifacts/6xgf8rml_connect%20band%20red.png' },
  { name: 'Mint Green', hex: '#98FB98', image: 'https://customer-assets.emergentagent.com/job_connect-premium-2/artifacts/2t3allwi_fitness%20band%20mint.png' },
];

// Typewriter component for premium text reveal
const TypewriterText = ({ text, delay = 0, onComplete }) => {
  const [displayedText, setDisplayedText] = useState('');
  const [showCaret, setShowCaret] = useState(true);
  const [isComplete, setIsComplete] = useState(false);
  const charIndex = useRef(0);

  useEffect(() => {
    const startTimeout = setTimeout(() => {
      const typeInterval = setInterval(() => {
        if (charIndex.current < text.length) {
          setDisplayedText(text.slice(0, charIndex.current + 1));
          charIndex.current++;
        } else {
          clearInterval(typeInterval);
          setIsComplete(true);
          if (onComplete) onComplete();
        }
      }, 50); // Premium typing speed

      return () => clearInterval(typeInterval);
    }, delay);

    return () => clearTimeout(startTimeout);
  }, [text, delay, onComplete]);

  // Caret blink effect
  useEffect(() => {
    if (!isComplete) {
      const blinkInterval = setInterval(() => {
        setShowCaret(prev => !prev);
      }, 530);
      return () => clearInterval(blinkInterval);
    } else {
      const hideTimeout = setTimeout(() => setShowCaret(false), 800);
      return () => clearTimeout(hideTimeout);
    }
  }, [isComplete]);

  return (
    <span>
      {displayedText}
      <span 
        className={`inline-block w-[3px] h-[0.85em] bg-[#1A1A1A] ml-1 align-middle transition-opacity duration-100 ${
          showCaret && !isComplete ? 'opacity-100' : 'opacity-0'
        }`}
      />
    </span>
  );
};

const HeroSection = ({ onReserveClick }) => {
  const [activeColor, setActiveColor] = useState(0);
  const [animationComplete, setAnimationComplete] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const interval = setInterval(() => {
      setActiveColor((prev) => (prev + 1) % productImages.length);
    }, 4000);
    return () => clearInterval(interval);
  }, []);

  const scrollToPlans = () => {
    document.getElementById('plans')?.scrollIntoView({ behavior: 'smooth' });
  };

  const goToHowItWorks = () => {
    navigate('/how-it-works');
  };

  return (
    <section className="hero-section" data-testid="hero-section">
      <div className="hero-bg" />

      <div className="container-custom relative z-10">
        <div className="grid lg:grid-cols-2 gap-8 lg:gap-12 items-center min-h-[calc(100vh-80px)] pt-[100px] pb-12">
          {/* Left Content */}
          <motion.div
            initial={{ opacity: 0, x: -40 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="order-2 lg:order-1"
          >
            {/* Main Headline with Typewriter Animation */}
            <h1 className="text-4xl sm:text-5xl lg:text-6xl xl:text-7xl font-semibold text-[#1A1A1A] leading-[1.1] mb-4">
              <TypewriterText 
                text="Closer. Safer. Healthier." 
                delay={400}
                onComplete={() => setAnimationComplete(true)}
              />
            </h1>

            {/* Positioning line - fades in after typewriter */}
            <motion.p 
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: animationComplete ? 1 : 0, y: animationComplete ? 0 : 10 }}
              transition={{ duration: 0.5, delay: 0.2 }}
              className="text-2xl sm:text-[1.7rem] lg:text-3xl text-[#5A5A5A] italic font-medium mb-7"
            >
              The first screenless wearable built for families.
            </motion.p>

            {/* Clarity line */}
            <motion.p 
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: animationComplete ? 1 : 0, y: animationComplete ? 0 : 10 }}
              transition={{ duration: 0.5, delay: 0.4 }}
              className="text-base sm:text-lg text-[#4A4A4A] mb-8 max-w-lg leading-relaxed"
            >
              Track your family's health, kids' safety, parents' wellness — and even pets — all in one family app.
            </motion.p>

            {/* CTA Buttons */}
            <motion.div 
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: animationComplete ? 1 : 0, y: animationComplete ? 0 : 10 }}
              transition={{ duration: 0.5, delay: 0.5 }}
              className="flex flex-col sm:flex-row gap-3 mb-4"
            >
              <motion.button
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                onClick={scrollToPlans}
                className="bg-[#1A1A1A] text-white px-8 py-3.5 rounded-full text-base font-medium
                           hover:bg-[#333] transition-all duration-300"
                data-testid="hero-plans-btn"
              >
                Choose Your Plan
              </motion.button>
              <motion.button
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                onClick={goToHowItWorks}
                className="bg-white border border-black/10 text-[#1A1A1A] px-8 py-3.5 
                           rounded-full text-base hover:bg-black/5 transition-all duration-300"
                data-testid="hero-how-btn"
              >
                How It Works
              </motion.button>
            </motion.div>

            {/* Subtle pricing text */}
            <motion.p 
              initial={{ opacity: 0 }}
              animate={{ opacity: animationComplete ? 1 : 0 }}
              transition={{ duration: 0.5, delay: 0.6 }}
              className="text-sm text-[#7A7A7A] mb-5"
            >
              Plans start at $99 • 1 year included
            </motion.p>

            {/* Trust Chips */}
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: animationComplete ? 1 : 0, y: animationComplete ? 0 : 10 }}
              transition={{ duration: 0.6, delay: 0.7 }}
              className="flex flex-wrap gap-3"
            >
              <div className="trust-chip" data-testid="trust-chip-battery">
                <Battery className="w-4 h-4" />
                <span>Up to 7-day battery</span>
              </div>
              <div className="trust-chip" data-testid="trust-chip-water">
                <Droplets className="w-4 h-4" />
                <span>Water resistant</span>
              </div>
              <div className="trust-chip" data-testid="trust-chip-platforms">
                <Smartphone className="w-4 h-4" />
                <span>iOS + Android</span>
              </div>
            </motion.div>
          </motion.div>

          {/* Right - Product Image */}
          <motion.div
            initial={{ opacity: 0, x: 40 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8, delay: 0.4 }}
            className="order-1 lg:order-2 relative"
          >
            <div className="relative aspect-square max-w-md mx-auto">
              <div className="relative w-full h-full flex items-center justify-center">
                {productImages.map((product, index) => (
                  <motion.img
                    key={product.name}
                    src={product.image}
                    alt={`Connect Band - ${product.name}`}
                    className={`absolute max-w-[80%] max-h-[80%] object-contain product-glow
                               transition-all duration-700 ${
                      index === activeColor ? 'opacity-100 scale-100' : 'opacity-0 scale-95'
                    }`}
                    initial={false}
                    animate={{
                      opacity: index === activeColor ? 1 : 0,
                      scale: index === activeColor ? 1 : 0.95,
                    }}
                  />
                ))}
              </div>

              <motion.p
                key={activeColor}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="absolute bottom-0 left-1/2 -translate-x-1/2 text-[#5A5A5A] text-sm"
              >
                {productImages[activeColor].name}
              </motion.p>
            </div>

            {/* Color Selector */}
            <div className="flex flex-wrap justify-center gap-2.5 mt-4">
              {productImages.map((product, index) => (
                <button
                  key={product.name}
                  onClick={() => setActiveColor(index)}
                  className={`color-dot ${index === activeColor ? 'active' : ''}`}
                  style={{ 
                    backgroundColor: product.hex,
                    border: product.hex === '#F5F5F5' ? '1px solid rgba(0,0,0,0.15)' : 'none'
                  }}
                  title={product.name}
                  data-testid={`color-dot-${index}`}
                />
              ))}
            </div>
          </motion.div>
        </div>

        {/* Scroll Indicator */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: animationComplete ? 1 : 0 }}
          transition={{ delay: 0.5 }}
          className="absolute bottom-6 left-1/2 -translate-x-1/2 hidden lg:flex flex-col items-center gap-2 cursor-pointer"
          onClick={scrollToPlans}
        >
          <span className="text-[#8A8A8A] text-xs uppercase tracking-widest">Scroll</span>
          <motion.div
            animate={{ y: [0, 8, 0] }}
            transition={{ duration: 1.5, repeat: Infinity }}
          >
            <ChevronDown className="w-5 h-5 text-[#8A8A8A]" />
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
};

export default HeroSection;
